import React from 'react'
import { ResponsiveContainer, Tooltip, Treemap } from 'recharts'
import styled from 'styled-components'

import {
  HOVER_DIMMED_OPACITY,
  HOVER_FOCUSED_OPACITY,
  HOVER_TRANSITION_CSS,
} from 'features/charts-v2/chart-hover-constants'
import {
  ChartDatapointHoverAnnotation,
  type ChartHoverAnnotationRow,
} from 'features/charts-v2/components/ChartDatapointHoverAnnotation'
import { dataTransformationService } from 'features/charts-v2/services/data-transformation-service'
import { BaseChartStrategy } from 'features/charts-v2/strategies/Base'
import {
  CustomTreemapCell,
  type TreemapCellProps,
} from 'features/charts-v2/strategies/CustomTreemapCell'
import { computeMetricTotals } from 'features/charts-v2/strategies/treemap-utils'
import { METRICS, type MetricType } from 'features/charts-v2/types'
import {
  ChartProps,
  TransformedChartData,
  TransformedDataPoint,
} from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import { buildBreakdownColorMap } from 'features/charts-v2/utils/breakdown-color-map'
import {
  formatMetricValue,
  getMetricLabel,
} from 'features/charts-v2/utils/metric-format-utils'

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;

  /* Dim non-hovered treemap cells when any cell is hovered */
  &:has(g[data-treemap-name]:hover) g[data-treemap-name] {
    opacity: ${HOVER_DIMMED_OPACITY};
    transition: ${HOVER_TRANSITION_CSS};
  }

  /* Keep the hovered cell bright */
  &:has(g[data-treemap-name]:hover) g[data-treemap-name]:hover {
    opacity: ${HOVER_FOCUSED_OPACITY} !important;
  }
`

export class TreemapChartStrategy extends BaseChartStrategy {
  public readonly chartType = ChartTypeV2.TREEMAP
  public readonly requiredMetrics: MetricType[] = []
  public readonly optionalMetrics: MetricType[] = [
    METRICS.RECORD_COUNT.id,
    METRICS.SENTENCE_COUNT.id,
    METRICS.SENTIMENT.id,
    METRICS.RATING.id,
    METRICS.NET_PROMOTER_SCORE.id,
  ]

  public transform(props: ChartProps): TransformedChartData {
    const breakdowns = props.selectedBreakdowns || []

    // Handle 0 breakdowns: show single treemap cell with total
    if (breakdowns.length === 0) {
      const totalData = dataTransformationService.transformForTotalOnly(
        props.data!,
        props.selectedMetrics,
      )

      // Transform to treemap format with size and metric values
      if (
        totalData.chartData &&
        totalData.chartData.length > 0 &&
        totalData.chartData[0]
      ) {
        const totalValue =
          (totalData.chartData[0] as { count?: number }).count || 0

        // Create a treemap data point with all selected metrics
        const treemapData: Record<string, string | number | boolean | null> = {
          name: 'Total',
          size: totalValue,
        }

        // Add each metric as a separate field
        props.selectedMetrics.forEach((metric) => {
          treemapData[`metric_${metric}`] = totalValue
        })

        return {
          chartData: [treemapData as TransformedDataPoint],
        }
      }
      return { chartData: [] }
    }

    return dataTransformationService.transformForTreemap(
      props.data!,
      props.selectedMetrics,
      props.timeBucket,
    )
  }

  public render(
    props: ChartProps & { transformedData: TransformedChartData },
  ): React.ReactElement {
    const validation = this.validate(props)
    if (!validation.isValid) {
      return this.renderValidationError(validation)
    }

    const {
      data,
      colorReferenceData,
      selectedBreakdowns,
      transformedData,
      onSegmentClick,
      selectedMetrics,
    } = props

    const breakdownColorMap =
      props.colorMap ??
      buildBreakdownColorMap(
        selectedBreakdowns?.[0] ?? null,
        colorReferenceData ?? data,
      )

    if (!transformedData.chartData || transformedData.chartData.length === 0) {
      return this.renderEmptyState(
        'No data available',
        'Try adding some filters.',
      )
    }

    // Precompute metric totals once for all cells
    const metricTotals = computeMetricTotals(
      transformedData.chartData as TransformedDataPoint[],
      selectedMetrics,
    )

    return (
      <ChartContainer>
        <ResponsiveContainer width="100%" height="100%">
          <Treemap
            data={transformedData.chartData}
            dataKey="size"
            aspectRatio={4 / 3}
            stroke="#fff"
            fill="#8884d8"
            content={(cellProps) => {
              const typedProps = cellProps as unknown as TreemapCellProps
              if (typedProps.depth !== 1) return <g />
              return (
                <CustomTreemapCell
                  {...typedProps}
                  selectedMetrics={selectedMetrics}
                  onSegmentClick={onSegmentClick}
                  metricTotals={metricTotals}
                  breakdownColorMap={breakdownColorMap}
                />
              )
            }}
            isAnimationActive={false}
          >
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload[0]) {
                  const data = payload[0].payload
                  const rows = selectedMetrics.flatMap<ChartHoverAnnotationRow>(
                    (metricType) => {
                      const metricValue = data[`metric_${metricType}`]
                      if (metricValue == null) return []
                      const isCountType =
                        metricType === 'record_count' ||
                        metricType === 'sentence_count'
                      const total = metricTotals[metricType]
                      if (isCountType && total != null && total > 0) {
                        const pct = ((metricValue / total) * 100).toFixed(2)
                        return [
                          {
                            label: getMetricLabel(metricType),
                            value: formatMetricValue(metricValue, metricType),
                            percentage: `${pct}%`,
                            color: null,
                          },
                        ]
                      }
                      return [
                        {
                          label: getMetricLabel(metricType),
                          value: formatMetricValue(metricValue, metricType),
                          percentage: null,
                          color: null,
                        },
                      ]
                    },
                  )
                  return (
                    <ChartDatapointHoverAnnotation
                      title={data.name}
                      rows={rows}
                      footer={null}
                      focusedField={null}
                    />
                  )
                }
                return null
              }}
            />
          </Treemap>
        </ResponsiveContainer>
      </ChartContainer>
    )
  }

  public getTitle(): string {
    return 'Category Hierarchy'
  }
}
